import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { styled } from "@mui/material/styles";

/** What the panel sends: "own" is the server's word for Mine, and stays it. */
export type TemplateOwnershipScope = "own" | "all";

const OwnershipToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  alignSelf: "flex-start",
  flexWrap: "wrap",
  maxWidth: "100%",
  "& .MuiToggleButtonGroup-grouped": {
    fontSize: "0.75rem",
    lineHeight: 1.2,
    padding: theme.spacing(0.5, 1),
    textTransform: "none",
  },
}));

type TemplateOwnershipFilterProps = {
  /**
   * What is being filtered, plural and lower case — "team templates", "documents". Both options'
   * accessible names are built from it, so the two read "All accessible team templates" and "Mine: my
   * team templates".
   */
  label: string;
  /**
   * The group's own accessible name, where a surface already has one that is addressed elsewhere.
   *
   * The document selector's group has been "Document ownership filter" since it was written, and
   * the browser suite reaches the conflicts and policies panels through exactly that name. Deriving
   * it from `label` would rename it to "documents ownership filter" and take those locators with it,
   * for no gain a reader would notice.
   */
  groupLabel?: string;
  value: TemplateOwnershipScope;
  onChange: (value: TemplateOwnershipScope) => void;
};

/**
 * Whose templates the panel is showing: everything the reader may reach, or the reader's own.
 *
 * Two options on every panel, All and Mine. An Entity scope sat between them until #5236, asking the
 * reader to decide whose library they wanted before anything had given them a reason to care — and
 * answering with a list All accessible already contains. The documents panel kept company choices,
 * the reader's and the other party's, until #7108: templates are the reader's and their entity's, so
 * it has these two as well.
 *
 * Mine, not Own (#7108). Beside All it reads as an answer on its own, where Own wants a noun after it.
 * Each accessible name contains the visible word (WCAG 2.5.3), so a reader who says what they see
 * reaches the button.
 *
 * **All is the default on every template panel, and is drawn first.** A panel that opens on Mine opens
 * on the narrower answer, so a reader with nothing of their own meets an empty table and no reason to
 * suspect the library is not empty too. Opening on everything they may reach shows them what there
 * is; Mine is then a narrowing they choose, which is the direction a filter is supposed to run. Each
 * panel's `useState` holds that default — this control is a controlled component and has no opinion
 * of its own — so the two have to be changed together.
 *
 * One control for every template panel in the app. It lives under `_shared` rather than beside the
 * proposal tabs because the document selector needs it too, and `_shared` may not be reached into
 * from a domain folder in the other direction.
 */
export default function TemplateOwnershipFilter(props: Readonly<TemplateOwnershipFilterProps>) {
  const { groupLabel, label, value, onChange } = props;
  const options: readonly { value: TemplateOwnershipScope; label: string; ariaLabel: string }[] = [
    { value: "all", label: "All", ariaLabel: `All accessible ${label}` },
    { value: "own", label: "Mine", ariaLabel: `Mine: my ${label}` },
  ];

  return (
    <OwnershipToggleButtonGroup
      aria-label={groupLabel ?? `${label} ownership filter`}
      color="primary"
      exclusive
      size="small"
      value={value}
      onChange={(_event, nextValue: TemplateOwnershipScope | null) => {
        if (nextValue) onChange(nextValue);
      }}
    >
      {options.map((option) => (
        <ToggleButton key={option.value} aria-label={option.ariaLabel} value={option.value}>
          {option.label}
        </ToggleButton>
      ))}
    </OwnershipToggleButtonGroup>
  );
}
